import { useMemo, useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Upload, Search, Loader2, ExternalLink, Trash2, BarChart3, X, Copy, Check, Star, FlaskConical, Eye, EyeOff, Save, Send, CheckSquare, Square, RefreshCw } from "lucide-react";
import { apiClient } from "../lib/api";
import { authHeaders, getBackendUrl } from "../lib/http";
import { callExtension } from "../lib/extension";
import { KeywordResearchTab } from "../components/research/KeywordResearchTab";
import type { ErankListing, ErankUploadRow, ErankTagsResponse, ErankShopStatsResponse, ErankShopStat } from "@erc/shared";

// ---- CSV ----
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { cur.push(field); field = ""; }
      else if (c === "\r") { /* skip */ }
      else if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; }
      else field += c;
    }
  }
  if (field.length || cur.length) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}
const toNum = (s: string | undefined) => Number(String(s ?? "").replace(/[$,]/g, "").trim()) || 0;
function shopNameFromFile(name: string): string {
  return name.replace(/\.csv$/i, "").replace(/\s*-\s*shop\s*listings\s*$/i, "").trim() || name;
}
// eRank ISO zaman damgasini ("2026-09-30T15:17:03.000000Z") okunur yap; diger bicimler aynen
function fmtErankDate(v?: string | null): string {
  if (!v) return "-";
  if (!/^\d{4}-\d{2}-\d{2}T/.test(v)) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function csvToRows(text: string): ErankUploadRow[] {
  const rows = parseCSV(text);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toUpperCase());
  const idx = (name: string) => header.indexOf(name);
  const iId = idx("LISTING ID"), iTitle = idx("LISTING"), iTags = idx("TAGS");
  const iViews = idx("TOTAL VIEWS"), iFav = idx("FAVORITES"), iSales = idx("EST. SALES");
  const iRev = idx("EST. REVENUE"), iPrice = idx("PRICE"), iAge = idx("LISTING AGE");
  const iDaily = idx("DAILY VIEWS"), iUpd = idx("LAST UPDATED");
  const out: ErankUploadRow[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const listingId = String(row[iId] ?? "").trim();
    if (!/^\d{3,}$/.test(listingId)) continue;
    out.push({
      listingId,
      title: (row[iTitle] ?? "").trim(),
      tags: iTags >= 0 ? (row[iTags] ?? "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean) : [],
      totalViews: toNum(row[iViews]),
      favorites: toNum(row[iFav]),
      estSales: toNum(row[iSales]),
      estRevenue: toNum(row[iRev]),
      price: toNum(row[iPrice]),
      listingAge: toNum(row[iAge]),
      dailyViews: toNum(row[iDaily]),
      lastUpdated: (row[iUpd] ?? "").trim(),
    });
  }
  return out;
}

// ---- Metrik tanimlari ----
const METRICS = [
  { key: "totalViews", label: "Görüntülenme", money: false },
  { key: "dailyViews", label: "Günlük Gör.", money: false },
  { key: "favorites", label: "Favori", money: false },
  { key: "estSales", label: "Tah. Satış", money: false },
  { key: "estRevenue", label: "Tah. Ciro", money: true },
  { key: "price", label: "Fiyat", money: true },
  { key: "listingAge", label: "Yaş (gün)", money: false },
  { key: "salesPerDay", label: "Satış/gün", money: false },
  { key: "conversion", label: "Dönüşüm %", money: false },
] as const;
type MetricKey = (typeof METRICS)[number]["key"];
// Yalniz filtrede kullanilan araliklar (Etsy'den tazelenince dolar)
const EXTRA_RANGES = [
  { key: "viewsGain", label: "Gör. artışı (Etsy)" },
  { key: "favGain", label: "Fav artışı (Etsy)" },
] as const;
const RANGE_FIELDS: { key: string; label: string }[] = [...METRICS, ...EXTRA_RANGES];

// Hazir filtre sablonlari: tek tikla ilgili min/max araliklarini kurar
const PRESETS: { label: string; hint: string; ranges: Record<string, { min: string; max: string }> }[] = [
  { label: "Yeni & satan", hint: "≤30 gün, en az 1 satış", ranges: { listingAge: { min: "", max: "30" }, estSales: { min: "1", max: "" } } },
  { label: "Hızlı satan", hint: "Günde ≥0,3 satış", ranges: { salesPerDay: { min: "0.3", max: "" } } },
  { label: "Yüksek dönüşüm", hint: "≥%3 dönüşüm, ≥100 görüntülenme", ranges: { conversion: { min: "3", max: "" }, totalViews: { min: "100", max: "" } } },
  { label: "Yükselen", hint: "≤60 gün, günlük ≥10 görüntülenme", ranges: { listingAge: { min: "", max: "60" }, dailyViews: { min: "10", max: "" } } },
];

const fmtInt = (n: number) => n.toLocaleString("tr-TR");
const fmtMoney = (n: number) => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function fmtDateTime(iso?: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
// Tazelik uyarisi: 30+ gun sari, 60+ gun kirmizi
function freshClass(iso?: string | null): string {
  if (!iso) return "text-gray-400";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days >= 60) return "text-red-500";
  if (days >= 30) return "text-amber-600";
  return "text-gray-400";
}

export default function ErankAnalysisPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);

  // Görünüm + filtreler
  const [view, setView] = useState<"list" | "tags" | "shops" | "keyword">("list");
  const [statWindow, setStatWindow] = useState<number>(7);
  const [shop, setShop] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<string>("totalViews");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [ranges, setRanges] = useState<Record<string, { min: string; max: string }>>({});
  const [tagSort, setTagSort] = useState<string>("totalSales");
  const [freshDays, setFreshDays] = useState<string>(""); // "" = tümü, "7"/"30"/"90"
  const [onlyFav, setOnlyFav] = useState(false);
  const [exclude, setExclude] = useState("");
  const [hideAnalyzed, setHideAnalyzed] = useState(false);
  const [hideViewed, setHideViewed] = useState(false);
  const [include, setInclude] = useState("");
  const [etsyState, setEtsyState] = useState(""); // "" | active | inactive | synced | unsynced
  const [pageSize, setPageSize] = useState<number>(() => Number(localStorage.getItem("erc-erank-pagesize")) || 50);
  useEffect(() => { try { localStorage.setItem("erc-erank-pagesize", String(pageSize)); } catch { /* */ } }, [pageSize]);

  // Toplu secim: kayit id -> { listingId, title } (sayfalar arasi korunur)
  const [selected, setSelected] = useState<Map<string, { listingId: string; title: string }>>(new Map());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<{ text: string; err?: boolean; task?: boolean } | null>(null);
  const toggleSel = (it: { id: string; listingId: string; title: string }) =>
    setSelected((p) => { const n = new Map(p); if (n.has(it.id)) n.delete(it.id); else n.set(it.id, { listingId: it.listingId, title: it.title }); return n; });

  // Gorsel onizleme: 44px kucuk gorselde urun secilemiyor, uzerine gelince
  // buyugu imlecin yaninda gosterilir. Tablo kaydirmali oldugu icin kutu
  // sayfaya (fixed) cizilir, hucrenin icine degil.
  const [preview, setPreview] = useState<{ url: string; x: number; y: number } | null>(null);
  const showPreview = (url: string, x: number, y: number) => setPreview({ url, x, y });
  const hidePreview = () => setPreview(null);

  // Eski veri temizligi: 96 bin satirlik birikim aramayi ve sayfalamayi
  // yavaslatiyor; yuklenme tarihine gore silinir.
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeBefore, setPurgeBefore] = useState("");
  const [purgeShopOnly, setPurgeShopOnly] = useState(false);
  const [purgeCount, setPurgeCount] = useState<number | null>(null);
  const [purgeBusy, setPurgeBusy] = useState(false);

  function purgeQuery() {
    const p = new URLSearchParams();
    if (purgeBefore) p.set("before", new Date(purgeBefore).toISOString());
    if (purgeShopOnly && shop) p.set("shop", shop);
    return p.toString();
  }

  async function previewPurge() {
    setPurgeBusy(true);
    try {
      const r = await fetch(`${getBackendUrl()}/api/erank/purge-count?${purgeQuery()}`, { headers: authHeaders() });
      const b = await r.json().catch(() => ({}));
      setPurgeCount(r.ok ? Number(b.data?.count ?? 0) : null);
    } catch { setPurgeCount(null); }
    finally { setPurgeBusy(false); }
  }

  async function runPurge() {
    if (!purgeBefore || !purgeCount) return;
    setPurgeBusy(true);
    try {
      const r = await fetch(`${getBackendUrl()}/api/erank?${purgeQuery()}`, { method: "DELETE", headers: authHeaders() });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || "Silinemedi");
      setUploadMsg(`${Number(b.data?.deleted ?? 0).toLocaleString("tr-TR")} eski kayıt silindi`);
      setPurgeCount(null);
      setPurgeOpen(false);
      qc.invalidateQueries({ queryKey: ["erank-listings"] });
      qc.invalidateQueries({ queryKey: ["erank-shops"] });
      qc.invalidateQueries({ queryKey: ["erank-tags"] });
      qc.invalidateQueries({ queryKey: ["erank-shop-stats"] });
    } catch (e) {
      setUploadMsg(`Hata: ${e instanceof Error ? e.message : "silinemedi"}`);
    } finally { setPurgeBusy(false); }
  }

  // Tarih/magaza degisince eski sayim yaniltmasin
  useEffect(() => { setPurgeCount(null); }, [purgeBefore, purgeShopOnly, shop]);

  // Kayıtlı filtreler (localStorage)
  const SF_KEY = "erc-erank-filters";
  type SavedFilter = { id: string; name: string; f: Record<string, unknown> };
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(() => {
    try { const s = localStorage.getItem(SF_KEY); const p = s ? JSON.parse(s) : []; return Array.isArray(p) ? p : []; } catch { return []; }
  });
  const [selectedFilterId, setSelectedFilterId] = useState("");
  useEffect(() => { try { localStorage.setItem(SF_KEY, JSON.stringify(savedFilters)); } catch { /* */ } }, [savedFilters]);

  const captureFilters = () => ({ shop, search, sort, order, ranges, freshDays, onlyFav, exclude, include, etsyState, hideAnalyzed, hideViewed });
  const applyFilters = (f: Record<string, unknown>) => {
    setShop((f.shop as string) ?? "");
    setSearch((f.search as string) ?? "");
    setSort((f.sort as string) ?? "totalViews");
    setOrder((f.order as "asc" | "desc") ?? "desc");
    setRanges((f.ranges as Record<string, { min: string; max: string }>) ?? {});
    setFreshDays((f.freshDays as string) ?? "");
    setOnlyFav(!!f.onlyFav);
    setExclude((f.exclude as string) ?? "");
    setHideAnalyzed(!!f.hideAnalyzed);
    setHideViewed(!!f.hideViewed);
    setInclude((f.include as string) ?? "");
    setEtsyState((f.etsyState as string) ?? "");
    setPage(1);
  };
  const saveCurrentFilter = () => {
    const name = window.prompt("Filtre adı:", "");
    if (!name || !name.trim()) return;
    const sf: SavedFilter = { id: `f_${Date.now()}`, name: name.trim(), f: captureFilters() };
    setSavedFilters((p) => [sf, ...p]);
    setSelectedFilterId(sf.id);
  };
  const onSelectFilter = (id: string) => {
    setSelectedFilterId(id);
    const sf = savedFilters.find((x) => x.id === id);
    if (sf) applyFilters(sf.f);
  };
  const deleteSelectedFilter = () => {
    if (!selectedFilterId) return;
    setSavedFilters((p) => p.filter((x) => x.id !== selectedFilterId));
    setSelectedFilterId("");
  };

  const setRange = (k: string, which: "min" | "max", v: string) =>
    setRanges((p) => ({ ...p, [k]: { ...(p[k] ?? { min: "", max: "" }), [which]: v } }));

  const filterParams = useMemo(() => {
    const q: Record<string, string | number> = {};
    if (shop) q.shop = shop;
    if (search) q.search = search;
    for (const m of RANGE_FIELDS) {
      const r = ranges[m.key];
      if (r?.min) q[`min_${m.key}`] = Number(r.min);
      if (r?.max) q[`max_${m.key}`] = Number(r.max);
    }
    if (freshDays) q.uploadedAfter = new Date(Date.now() - Number(freshDays) * 86400000).toISOString();
    if (onlyFav) q.onlyFav = "1";
    if (exclude.trim()) q.exclude = exclude.trim();
    if (include.trim()) q.include = include.trim();
    if (hideViewed) q.onlyUnviewed = "1";
    if (etsyState) q.etsyState = etsyState;
    return q;
  }, [shop, search, ranges, freshDays, onlyFav, exclude, include, hideViewed, etsyState]);

  const listQuery = useMemo(() => ({ ...filterParams, sort, order, page, limit: pageSize }), [filterParams, sort, order, page, pageSize]);
  const tagsQuery = useMemo(() => ({ ...filterParams, sort: tagSort, limit: 250 }), [filterParams, tagSort]);

  const { data: shops = [] } = useQuery({ queryKey: ["erank-shops"], queryFn: () => apiClient.getErankShops() });
  const { data: listData, isLoading } = useQuery({
    queryKey: ["erank-list", listQuery],
    queryFn: () => apiClient.getErankListings(listQuery),
    enabled: view === "list",
  });
  const { data: tagData, isLoading: tagsLoading } = useQuery({
    queryKey: ["erank-tags", tagsQuery],
    queryFn: () => apiClient.getErankTags(tagsQuery),
    enabled: view === "tags",
  });
  const { data: shopStats, isLoading: shopStatsLoading } = useQuery({
    queryKey: ["erank-shop-stats", statWindow],
    queryFn: () => apiClient.getErankShopStats(statWindow),
    enabled: view === "shops",
  });
  const [aiInsight, setAiInsight] = useState<string>("");
  const aiInsightM = useMutation({
    mutationFn: (model?: string) => apiClient.getErankAiInsight(statWindow, model),
    onSuccess: (r) => setAiInsight(r.insight || ""),
  });

  const openShop = (shopName: string) => { setShop(shopName); setView("list"); setPage(1); };
  const openTag = (tag: string) => { setSearch(tag); setView("list"); setPage(1); };

  // Analiz durumu senkron: gorunen listing'lerin hangileri analiz edilmis?
  const listingIds = useMemo(() => (listData?.data ?? []).map((l) => l.listingId), [listData]);
  const { data: analyzedMap = {} } = useQuery({
    queryKey: ["erank-analyzed", listingIds],
    queryFn: () => apiClient.lookupAnalysesBulkMeta(listingIds),
    enabled: view === "list" && listingIds.length > 0,
    // Toplu analiz arka planda ilerlerken rozetler tazelensin
    refetchInterval: 60000,
  });

  // ---- Toplu islemler ----
  const pageRows = listData?.data ?? [];
  const pageAllSelected = pageRows.length > 0 && pageRows.every((it) => selected.has(it.id));
  const togglePageSel = () => setSelected((p) => {
    const n = new Map(p);
    if (pageRows.every((it) => n.has(it.id))) pageRows.forEach((it) => n.delete(it.id));
    else pageRows.forEach((it) => n.set(it.id, { listingId: it.listingId, title: it.title }));
    return n;
  });
  async function selectAllMatching() {
    setBulkBusy(true); setBulkMsg(null);
    try {
      const r = await apiClient.getErankIds({ ...filterParams, sort, order, limit: 500 });
      setSelected((p) => { const n = new Map(p); for (const x of r.rows) n.set(x.id, { listingId: x.listingId, title: x.title }); return n; });
      setBulkMsg({ text: r.total > r.rows.length ? `Filtreye uyan ${fmtInt(r.total)} kaydın ilk ${r.rows.length} tanesi (mevcut sıralamaya göre) seçildi.` : `${r.rows.length} kayıt seçildi.` });
    } catch (e) { setBulkMsg({ text: e instanceof Error ? e.message : "Seçilemedi", err: true }); }
    finally { setBulkBusy(false); }
  }
  async function runBulk(action: "favorite" | "unfavorite" | "viewed" | "unviewed" | "delete") {
    const ids = [...selected.keys()];
    if (!ids.length) return;
    if (action === "delete" && !confirm(`${ids.length} kayıt eRank verisinden silinsin mi?`)) return;
    setBulkBusy(true); setBulkMsg(null);
    try {
      const r = await apiClient.erankBulk(ids, action);
      const label = { favorite: "favoriye eklendi", unfavorite: "favoriden çıkarıldı", viewed: "incelendi işaretlendi", unviewed: "incelenmedi yapıldı", delete: "silindi" }[action];
      setBulkMsg({ text: `${r.count} kayıt ${label}.` });
      if (action === "delete") setSelected(new Map());
      qc.invalidateQueries({ queryKey: ["erank-list"] });
      if (action === "delete") qc.invalidateQueries({ queryKey: ["erank-shops"] });
    } catch (e) { setBulkMsg({ text: e instanceof Error ? e.message : "İşlem başarısız", err: true }); }
    finally { setBulkBusy(false); }
  }
  // Secilenleri eklentide toplu analiz gorevine cevir: analiz edilmisler atlanir,
  // eklenti her urunu arka plan sekmesinde acip okur ve analiz kuyruguna ekler.
  async function sendToAnalysis() {
    const list = [...selected.values()];
    if (!list.length) return;
    setBulkBusy(true); setBulkMsg(null);
    try {
      const analyzed: Record<string, unknown> = {};
      for (let i = 0; i < list.length; i += 400) {
        Object.assign(analyzed, await apiClient.lookupAnalysesBulkMeta(list.slice(i, i + 400).map((x) => x.listingId)));
      }
      const todo = list.filter((x) => !analyzed[x.listingId]);
      const already = list.length - todo.length;
      if (!todo.length) { setBulkMsg({ text: `Seçilen ${list.length} ürünün hepsi zaten analiz edilmiş.` }); return; }
      if (!confirm(`${todo.length} ürün eklentide analiz kuyruğuna eklenecek${already ? ` (${already} tanesi zaten analizli, atlanacak)` : ""}.\nEklenti her ürünü arka planda açıp okuyacak; tarayıcı açık kalmalı. Custom (kişiselleştirmeli) ürünler analiz edilmez, eklentide karar listesine alınır. Devam?`)) return;
      const r = await callExtension<{ success?: boolean; added?: number; skipped?: number; queued?: number }>({
        type: "ENQUEUE_BULK_ANALYSIS",
        payload: { items: todo.map((x) => ({ url: `https://www.etsy.com/listing/${x.listingId}`, title: x.title })) },
      });
      if (!r?.success) {
        setBulkMsg({ text: "Eklentiye ulaşılamadı. Eklentinin yüklü ve güncel olduğundan emin ol; olmazsa panelden çıkış yapıp tekrar giriş yap (eklenti bağlantısı girişte kurulur).", err: true });
        return;
      }
      setBulkMsg({ task: true, text: `✓ ${r.added ?? 0} ürün eklentide analiz görevine eklendi${r.skipped ? `, ${r.skipped} tanesi zaten kuyrukta` : ""}${already ? `, ${already} analizli atlandı` : ""}. İlerlemeyi eklenti panelindeki Analiz Kuyruğu'ndan izleyebilirsin.` });
      setSelected(new Map());
    } catch (e) {
      setBulkMsg({ text: e instanceof Error ? e.message : "Gönderilemedi", err: true });
    } finally { setBulkBusy(false); }
  }
  // Etsy Open API ile guncel degerleri cek: secim varsa secilenler, yoksa filtreye uyanlarin ilk 500'u
  async function refreshFromEtsy(mode: "selected" | "filtered") {
    setBulkBusy(true); setBulkMsg({ text: "Etsy'den güncel veriler çekiliyor…" });
    try {
      const r = await apiClient.refreshErankFromEtsy(mode === "selected"
        ? { ids: [...selected.keys()] }
        : { filters: filterParams, sort, order, limit: 500 });
      setBulkMsg({ text: `Etsy'den güncellendi: ${r.updated} ürün${r.removed ? `, ${r.removed} tanesi Etsy'de yok (kaldırılmış/süresi dolmuş)` : ""}${r.skipped ? `, ${r.skipped} tanesi son 1 saatte zaten güncellenmiş` : ""} · ${r.calls} API çağrısı.` });
      qc.invalidateQueries({ queryKey: ["erank-list"] });
    } catch (e) { setBulkMsg({ text: e instanceof Error ? e.message : "Etsy'den çekilemedi", err: true }); }
    finally { setBulkBusy(false); }
  }
  async function stopBulkAnalysis() {
    const r = await callExtension<{ success?: boolean; removed?: number }>({ type: "STOP_BULK_ANALYSIS" });
    setBulkMsg(r?.success ? { text: `Toplu görev durduruldu: sırada bekleyen ${r.removed ?? 0} iş kaldırıldı (çalışan iş tamamlanır).` } : { text: "Eklentiye ulaşılamadı.", err: true });
  }

  const favM = useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) => apiClient.setErankFavorite(id, favorite),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["erank-list"] }),
  });

  // Görüldü işareti (başlık/Etsy tıklanınca) — anında + kalıcı
  const [viewedSet, setViewedSet] = useState<Set<string>>(new Set());
  const markViewed = (id: string) => {
    setViewedSet((p) => { if (p.has(id)) return p; const n = new Set(p); n.add(id); return n; });
    apiClient.markErankViewed(id).catch(() => {});
  };

  const uploadM = useMutation({
    mutationFn: async (files: File[]) => {
      const results: string[] = [];
      for (const file of files) {
        try {
          const text = await file.text();
          const rows = csvToRows(text);
          if (rows.length === 0) { results.push(`⚠ ${file.name}: satır/başlık okunamadı (CSV formatı beklenenden farklı olabilir)`); continue; }
          const shopName = shopNameFromFile(file.name);
          const r = await apiClient.uploadErankListings(shopName, rows);
          results.push(`✓ ${shopName}: ${r.added} yeni, ${r.updated} güncellendi${r.skipped ? `, ${r.skipped} atlandı` : ""} (${rows.length} satır)`);
        } catch (e) {
          results.push(`✗ ${file.name}: ${e instanceof Error ? e.message : "yükleme hatası"}`);
        }
      }
      return results;
    },
    onSuccess: (results) => {
      setUploadMsg(results.join("\n"));
      qc.invalidateQueries({ queryKey: ["erank-shops"] });
      qc.invalidateQueries({ queryKey: ["erank-list"] });
      qc.invalidateQueries({ queryKey: ["erank-tags"] });
    },
    onError: (e) => setUploadMsg(`Hata: ${e instanceof Error ? e.message : "yükleme başarısız"}`),
    onSettled: () => setUploading(false),
  });

  const deleteM = useMutation({
    mutationFn: (s?: string) => apiClient.deleteErankListings(s),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["erank-shops"] });
      qc.invalidateQueries({ queryKey: ["erank-list"] });
    },
  });

  function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter((f) => /\.csv$/i.test(f.name) || f.type.includes("csv"));
    if (arr.length === 0) { setUploadMsg("✗ Seçilen dosyalar .csv değil"); return; }
    setUploading(true); setUploadMsg(null);
    uploadM.mutate(arr);
    if (fileRef.current) fileRef.current.value = "";
  }

  const items = listData?.data ?? [];
  const visibleItems = useMemo(() => items.filter((it) => {
    if (hideAnalyzed && analyzedMap[it.listingId]) return false;
    if (hideViewed && viewedSet.has(it.id)) return false; // sunucu incelenmemisleri getirir; bu oturumda tiklananlar da gizlensin
    return true;
  }), [items, hideAnalyzed, hideViewed, analyzedMap, viewedSet]);
  const total = listData?.total ?? 0;
  const pageCount = listData?.pageCount ?? 1;
  const activeFilters = !!shop || !!search || !!freshDays || !!exclude.trim() || !!include.trim() || onlyFav || !!etsyState || RANGE_FIELDS.some((m) => ranges[m.key]?.min || ranges[m.key]?.max);

  const toggleSort = (k: MetricKey) => {
    if (sort === k) setOrder((o) => (o === "desc" ? "asc" : "desc"));
    else { setSort(k); setOrder("desc"); }
    setPage(1);
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900"><BarChart3 className="h-5 w-5 text-indigo-600" /> eRank Analiz</h1>
          <p className="text-sm text-gray-500">Satıcı "Shop listings" CSV'lerini yükle, mağaza bazlı veya tüm mağazalarda filtrele.</p>
        </div>
        <div className="flex flex-shrink-0 flex-col items-end gap-1">
          <input ref={fileRef} type="file" accept=".csv,text/csv" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            title="Birden fazla CSV seçebilirsin. Aynı Listing ID tekrar yüklenirse çoğaltılmaz, verisi güncellenir."
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Yükleniyor…" : "CSV Yükle"}
          </button>
          {/* Eski kayitlar birikiyor (96 bin satir): yuklenme tarihine gore temizle */}
          <button
            onClick={() => setPurgeOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" /> Eski verileri sil
          </button>
        </div>
      </div>

      {preview && (() => {
        // Imlecin sagina siger mi? Sigmazsa soluna al ki ekran disina tasmasin
        const W = 320, H = 320, PAD = 16;
        const right = preview.x + PAD;
        const left = right + W > window.innerWidth ? preview.x - W - PAD : right;
        const top = Math.min(Math.max(PAD, preview.y - H / 2), Math.max(PAD, window.innerHeight - H - PAD));
        return (
          <div
            className="pointer-events-none fixed z-50 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
            style={{ left, top, width: W, height: H }}
          >
            <img src={preview.url} alt="" referrerPolicy="no-referrer" className="h-full w-full object-contain" />
          </div>
        );
      })()}

      {purgeOpen && (
        <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
          <p className="mb-2 text-xs font-semibold text-red-800">Eski eRank verilerini sil</p>
          <p className="mb-2 text-[11px] text-red-700">
            Seçilen tarihten ÖNCE yüklenmiş (veya o tarihten beri tazelenmemiş) kayıtlar silinir.
            Aynı listing sonradan tekrar yüklenirse tarihi güncellenir, yani aktif takip ettiklerin silinmez.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={purgeBefore}
              onChange={(e) => setPurgeBefore(e.target.value)}
              className="rounded border border-gray-200 px-2 py-1 text-xs"
            />
            {[30, 60, 90].map((d) => (
              <button
                key={d}
                onClick={() => {
                  const dt = new Date();
                  dt.setDate(dt.getDate() - d);
                  setPurgeBefore(dt.toISOString().slice(0, 10));
                }}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
              >
                {d} günden eski
              </button>
            ))}
            <label className="flex items-center gap-1 text-[11px] text-gray-600">
              <input type="checkbox" checked={purgeShopOnly} onChange={(e) => setPurgeShopOnly(e.target.checked)} />
              yalnızca seçili mağaza
            </label>
            <button
              onClick={previewPurge}
              disabled={!purgeBefore || purgeBusy}
              className="rounded border border-red-300 bg-white px-2.5 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-40"
            >
              Kaç kayıt?
            </button>
            <button
              onClick={runPurge}
              disabled={!purgeBefore || purgeBusy || purgeCount === null || purgeCount === 0}
              className="rounded bg-red-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-red-700 disabled:opacity-40"
            >
              {purgeBusy ? "Siliniyor…" : purgeCount ? `${purgeCount.toLocaleString("tr-TR")} kaydı sil` : "Sil"}
            </button>
            <button onClick={() => setPurgeOpen(false)} className="text-[11px] text-gray-500 hover:text-gray-700">Kapat</button>
          </div>
          {purgeCount !== null && !purgeBusy && (
            <p className="mt-1.5 text-[11px] text-red-700">
              {purgeCount === 0 ? "Bu tarihten eski kayıt yok." : `${purgeCount.toLocaleString("tr-TR")} kayıt silinecek.`}
            </p>
          )}
        </div>
      )}

      {uploadMsg && (
        <div className={`flex items-start gap-2 whitespace-pre-line rounded-lg border px-3 py-2 text-xs ${uploadMsg.includes("✗") || uploadMsg.startsWith("Hata") ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          <span className="flex-1">{uploadMsg}</span>
          <button onClick={() => setUploadMsg(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Filtreler */}
      <div className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3 ${view === "keyword" ? "hidden" : ""}`}>
        {/* Kayıtlı filtreler */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-3">
          <span className="text-xs font-semibold text-gray-500">Kayıtlı filtre:</span>
          <select value={selectedFilterId} onChange={(e) => onSelectFilter(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
            <option value="">— seç —</option>
            {savedFilters.map((sf) => <option key={sf.id} value={sf.id}>{sf.name}</option>)}
          </select>
          {selectedFilterId && (
            <>
              <button onClick={() => { setSavedFilters((p) => p.map((x) => x.id === selectedFilterId ? { ...x, f: captureFilters() } : x)); }}
                className="text-xs text-indigo-600 hover:underline">Güncelle</button>
              <button onClick={deleteSelectedFilter} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /> Sil</button>
            </>
          )}
          <button onClick={saveCurrentFilter}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700">
            <Save className="h-3.5 w-3.5" /> Filtreyi Kaydet
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ShopPicker shops={shops} value={shop} onChange={(v) => { setShop(v); setPage(1); }} />
          <select value={etsyState} onChange={(e) => { setEtsyState(e.target.value); setPage(1); }}
            title="Etsy Open API'den tazelenen duruma göre"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
            <option value="">Etsy durumu: tümü</option>
            <option value="active">Etsy'de aktif</option>
            <option value="inactive">Etsy'de yok / pasif</option>
            <option value="synced">Etsy'den tazelenmiş</option>
            <option value="unsynced">Hiç tazelenmemiş</option>
          </select>
          <select value={freshDays} onChange={(e) => { setFreshDays(e.target.value); setPage(1); }}
            title="Veri tazeliği: sadece bu süre içinde yüklenen/tazelenen veriler"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
            <option value="">Tüm veriler (tazelik)</option>
            <option value="7">Son 7 günde yüklenen</option>
            <option value="14">Son 14 gün</option>
            <option value="30">Son 30 gün</option>
            <option value="90">Son 90 gün</option>
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Başlık, etiket veya ID ara…"
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
          <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
            {METRICS.map((m) => <option key={m.key} value={m.key}>Sırala: {m.label}</option>)}
            <option value="updatedAt">Sırala: Yüklenme Tarihi</option>
            <option value="lastUpdatedAt">Sırala: Son Güncelleme</option>
            <option value="viewsGain">Sırala: Gör. artışı (Etsy)</option>
            <option value="favGain">Sırala: Fav artışı (Etsy)</option>
            <option value="etsySyncedAt">Sırala: Etsy tazeleme</option>
          </select>
          <button onClick={() => { setOrder((o) => (o === "desc" ? "asc" : "desc")); setPage(1); }}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
            {order === "desc" ? "↓ Azalan" : "↑ Artan"}
          </button>
          <button onClick={() => { setOnlyFav((v) => !v); setPage(1); }}
            title="Sadece favoriler"
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${onlyFav ? "border-amber-400 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            <Star className={`h-3.5 w-3.5 ${onlyFav ? "fill-amber-400 text-amber-500" : ""}`} /> Favoriler
          </button>
          <button onClick={() => setHideAnalyzed((v) => !v)} title="Analiz edilenleri gizle (bu sayfada)"
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${hideAnalyzed ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            <FlaskConical className="h-3.5 w-3.5" /> Analizli gizle
          </button>
          <button onClick={() => { setHideViewed((v) => !v); setPage(1); }} title="İncelenenleri (görülenleri) gizle"
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${hideViewed ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            <Eye className="h-3.5 w-3.5" /> İncelenen gizle
          </button>
          {activeFilters && (
            <button onClick={() => { setShop(""); setSearch(""); setRanges({}); setFreshDays(""); setExclude(""); setInclude(""); setEtsyState(""); setOnlyFav(false); setPage(1); }}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-500"><X className="h-3.5 w-3.5" /> Temizle</button>
          )}
        </div>
        {/* Hariç tut */}
        <div className="relative">
          <X className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-red-400" />
          <input value={exclude} onChange={(e) => { setExclude(e.target.value); setPage(1); }}
            placeholder="Gizle (başlık/tag'de geçenler): ör. custom, personalized, name"
            className="w-full rounded-lg border border-red-200 bg-red-50/40 py-2 pl-9 pr-3 text-sm placeholder:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-200" />
        </div>
        {/* Icersin */}
        <div className="relative">
          <Check className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-emerald-500" />
          <input value={include} onChange={(e) => { setInclude(e.target.value); setPage(1); }}
            placeholder="İçersin (başlık/tag'de en az biri): ör. funny, teacher, dog mom"
            className="w-full rounded-lg border border-emerald-200 bg-emerald-50/40 py-2 pl-9 pr-3 text-sm placeholder:text-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200" />
        </div>
        {/* Hazir sablonlar */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold text-gray-500">Hazır:</span>
          {PRESETS.map((p) => (
            <button key={p.label} title={p.hint}
              onClick={() => { setRanges((r) => ({ ...r, ...p.ranges })); setPage(1); }}
              className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
              {p.label}
            </button>
          ))}
        </div>
        {/* Min/Max */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-11">
          {RANGE_FIELDS.map((m) => (
            <div key={m.key} className="rounded-lg border border-gray-100 bg-gray-50/50 p-2">
              <div className="mb-1 text-[10px] font-semibold text-gray-500">{m.label}</div>
              <div className="flex items-center gap-1">
                <input type="number" step="any" placeholder="min" value={ranges[m.key]?.min ?? ""}
                  onChange={(e) => { setRange(m.key, "min", e.target.value); setPage(1); }}
                  className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px]" />
                <span className="text-gray-300">–</span>
                <input type="number" step="any" placeholder="max" value={ranges[m.key]?.max ?? ""}
                  onChange={(e) => { setRange(m.key, "max", e.target.value); setPage(1); }}
                  className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px]" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Toplu islem cubugu */}
      {(selected.size > 0 || bulkMsg) && (
        <div className="sticky top-0 z-20 space-y-2 rounded-xl border border-indigo-200 bg-indigo-50/95 px-4 py-2.5 shadow-sm backdrop-blur">
          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-indigo-800">{fmtInt(selected.size)} seçili</span>
              <button onClick={sendToAnalysis} disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Toplu analize gönder
              </button>
              <button onClick={() => runBulk("favorite")} disabled={bulkBusy} className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"><Star className="h-3.5 w-3.5" /> Favoriye ekle</button>
              <button onClick={() => runBulk("unfavorite")} disabled={bulkBusy} className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50">Favoriden çıkar</button>
              <button onClick={() => runBulk("viewed")} disabled={bulkBusy} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-white px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"><Eye className="h-3.5 w-3.5" /> İncelendi</button>
              <button onClick={() => runBulk("unviewed")} disabled={bulkBusy} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"><EyeOff className="h-3.5 w-3.5" /> İncelenmedi</button>
              <button onClick={() => runBulk("delete")} disabled={bulkBusy} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /> Sil</button>
              <button onClick={() => refreshFromEtsy("selected")} disabled={bulkBusy} title="Seçilenlerin güncel görüntülenme/favori/fiyat/durumunu Etsy'den çek"
                className="inline-flex items-center gap-1 rounded-lg border border-orange-300 bg-white px-2.5 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50"><RefreshCw className="h-3.5 w-3.5" /> Etsy'den güncelle</button>
              <button onClick={() => setSelected(new Map())} className="ml-auto text-xs text-gray-500 hover:text-gray-700">Seçimi temizle</button>
            </div>
          )}
          {bulkMsg && (
            <div className={`flex items-start gap-2 text-xs ${bulkMsg.err ? "text-red-700" : "text-indigo-800"}`}>
              <span className="flex-1">{bulkMsg.text}</span>
              {bulkMsg.task && (
                <button onClick={stopBulkAnalysis} className="shrink-0 rounded border border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50">Görevi durdur</button>
              )}
              <button onClick={() => setBulkMsg(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      )}

      {/* Sekmeler */}
      <div className="flex items-center gap-2">
        <button onClick={() => setView("list")} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${view === "list" ? "bg-indigo-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>Listeler</button>
        <button onClick={() => setView("tags")} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${view === "tags" ? "bg-indigo-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>Tag Analizi</button>
        <button onClick={() => setView("shops")} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${view === "shops" ? "bg-indigo-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>Mağaza İstatistikleri</button>
        <button onClick={() => setView("keyword")} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${view === "keyword" ? "bg-indigo-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>Keyword</button>
      </div>

      {/* Tablo */}
      {view === "keyword" ? (
        <KeywordResearchTab />
      ) : view === "shops" ? (
        <ShopsView
          data={shopStats} loading={shopStatsLoading}
          statWindow={statWindow} setStatWindow={setStatWindow}
          aiInsight={aiInsight} aiLoading={aiInsightM.isPending} aiError={aiInsightM.error instanceof Error ? aiInsightM.error.message : ""}
          onRunAi={(model) => aiInsightM.mutate(model)} onOpenShop={openShop}
        />
      ) : view === "tags" ? (
        <TagsView data={tagData} loading={tagsLoading} tagSort={tagSort} setTagSort={setTagSort} onOpenTag={openTag} />
      ) : isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center text-sm text-gray-400">
          {activeFilters ? "Filtreye uyan listing yok." : "Henüz veri yok. Yukarıdan bir eRank CSV yükle."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">{fmtInt(total)} listing{shop ? ` · ${shop}` : ""}</span>
              <button onClick={selectAllMatching} disabled={bulkBusy} title="Filtreye uyan kayıtları (mevcut sıralamayla, en fazla 500) seç"
                className="text-[11px] font-medium text-indigo-600 hover:underline disabled:opacity-40">Filtreye uyanları seç</button>
              <button onClick={() => refreshFromEtsy("filtered")} disabled={bulkBusy} title="Filtreye uyanların ilk 500'ünün (mevcut sıralamayla) güncel verisini Etsy Open API'den çek"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-orange-600 hover:underline disabled:opacity-40">
                <RefreshCw className={`h-3 w-3 ${bulkBusy ? "animate-spin" : ""}`} /> Etsy'den güncelle
              </button>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-600">
                {[50, 100, 200].map((n) => <option key={n} value={n}>{n}/sayfa</option>)}
              </select>
            </div>
            {shop && (
              <button onClick={() => { if (confirm(`"${shop}" mağazasının tüm verisi silinsin mi?`)) deleteM.mutate(shop); }}
                className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /> Bu mağazayı sil</button>
            )}
          </div>
          <div className="max-h-[72vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-200 bg-gray-100 text-left text-[11px] uppercase text-gray-500 shadow-sm">
                  <th className="w-8 px-2 py-2">
                    <button onClick={togglePageSel} title="Bu sayfayı seç / bırak" className="text-gray-400 hover:text-indigo-600">
                      {pageAllSelected ? <CheckSquare className="h-4 w-4 text-indigo-600" /> : <Square className="h-4 w-4" />}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">Durum</th>
                  <th className="px-3 py-2 font-medium">Ürün</th>
                  <th className="px-3 py-2 font-medium">Mağaza</th>
                  {METRICS.map((m) => (
                    <th key={m.key} onClick={() => toggleSort(m.key)}
                      className="cursor-pointer whitespace-nowrap px-3 py-2 text-right font-medium hover:text-indigo-600">
                      {m.label}{sort === m.key ? (order === "desc" ? " ↓" : " ↑") : ""}
                    </th>
                  ))}
                  <th className="px-3 py-2 font-medium" title="Etsy Open API'den çekilen güncel değerler (artış = eRank'ten bu yana)">Etsy (güncel)</th>
                  <th className="px-3 py-2 font-medium">eRank Güncel.</th>
                  <th className="px-3 py-2 font-medium">Yüklendi</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visibleItems.map((it) => (
                  <Row key={it.id} it={it} analyzed={analyzedMap[it.listingId]}
                    selected={selected.has(it.id)} onToggleSel={() => toggleSel(it)}
                    viewed={viewedSet.has(it.id) || !!it.viewedAt}
                    onView={() => markViewed(it.id)}
                    onToggleFav={() => favM.mutate({ id: it.id, favorite: !it.favorite })}
                    showPreview={showPreview}
                    hidePreview={hidePreview} />
                ))}
                {visibleItems.length === 0 && items.length > 0 && (
                  <tr><td colSpan={17} className="px-3 py-6 text-center text-xs text-gray-400">Bu sayfadaki tüm kayıtlar gizlendi (analiz/incelenen filtresi). Sonraki sayfaya geç.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white disabled:opacity-40">← Önceki</button>
              <span className="text-xs text-gray-500">Sayfa {page}/{pageCount}</span>
              <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white disabled:opacity-40">Sonraki →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Etsy guncel deger hucresi ----
const ETSY_STATE: Record<string, { label: string; cls: string }> = {
  removed: { label: "Kaldırılmış", cls: "bg-red-100 text-red-700" },
  sold_out: { label: "Tükendi", cls: "bg-amber-100 text-amber-700" },
  inactive: { label: "Pasif", cls: "bg-gray-200 text-gray-600" },
  expired: { label: "Süresi doldu", cls: "bg-gray-200 text-gray-600" },
  draft: { label: "Taslak", cls: "bg-gray-200 text-gray-600" },
};
function EtsyCell({ it }: { it: ErankListing }) {
  if (!it.etsySyncedAt) return <span className="text-gray-300">—</span>;
  const st = it.etsyState && it.etsyState !== "active" ? ETSY_STATE[it.etsyState] ?? { label: it.etsyState, cls: "bg-gray-200 text-gray-600" } : null;
  const gain = (n: number) => (n > 0 ? <span className="text-emerald-600"> +{fmtInt(n)}</span> : n < 0 ? <span className="text-red-500"> {fmtInt(n)}</span> : null);
  const ago = Math.floor((Date.now() - new Date(it.etsySyncedAt).getTime()) / 3600000);
  return (
    <div className="leading-tight" title={`Etsy'den ${ago < 1 ? "az önce" : ago < 48 ? `${ago} saat önce` : `${Math.floor(ago / 24)} gün önce`} tazelendi`}>
      {st ? <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}</span> : (
        <>
          <div className="tabular-nums text-gray-700">👁 {fmtInt(it.etsyViews ?? 0)}{gain(it.viewsGain)}</div>
          <div className="tabular-nums text-gray-500">♥ {fmtInt(it.etsyFavorites ?? 0)}{gain(it.favGain)}</div>
          {it.etsyPrice != null && Math.abs(it.etsyPrice - it.price) > 0.009 && <div className="text-gray-400">${it.etsyPrice.toFixed(2)}</div>}
        </>
      )}
    </div>
  );
}

// ---- Aranabilir mağaza seçici ----
function ShopPicker({ shops, value, onChange }: {
  shops: { shopName: string; count: number; lastUploadedAt: string | null }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const totalCount = shops.reduce((a, s) => a + s.count, 0);

  // Disari tiklayinca kapan
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useEffect(() => { if (open) { setQ(""); setHi(0); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);

  const term = q.trim().toLowerCase();
  const filtered = term ? shops.filter((s) => s.shopName.toLowerCase().includes(term)) : shops;
  // Klavye: ilk satir "Tum Magazalar" (yalniz arama bosken)
  const options: { value: string; label: string; sub?: string }[] = [
    ...(term ? [] : [{ value: "", label: `Tüm Mağazalar (${fmtInt(totalCount)})` }]),
    ...filtered.map((s) => ({ value: s.shopName, label: `${s.shopName} (${fmtInt(s.count)})`, sub: fmtDateTime(s.lastUploadedAt) })),
  ];
  const pick = (v: string) => { onChange(v); setOpen(false); };
  const current = value ? shops.find((s) => s.shopName === value) : null;

  return (
    <div ref={boxRef} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex min-w-[240px] max-w-[340px] items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:bg-gray-50">
        <span className="flex-1 truncate">
          {value ? `${value}${current ? ` (${fmtInt(current.count)})` : ""}` : `Tüm Mağazalar (${fmtInt(totalCount)})`}
        </span>
        <span className="text-[10px] text-gray-400">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-[360px] rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="relative border-b border-gray-100 p-2">
            <Search className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setHi(0); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(options.length - 1, h + 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
                else if (e.key === "Enter") { e.preventDefault(); if (options[hi]) pick(options[hi].value); }
                else if (e.key === "Escape") setOpen(false);
              }}
              placeholder={`Mağaza ara… (${shops.length})`}
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {options.length === 0 && <div className="px-3 py-3 text-center text-xs text-gray-400">Eşleşen mağaza yok</div>}
            {options.map((o, i) => (
              <button key={o.value || "_all"} type="button" onClick={() => pick(o.value)} onMouseEnter={() => setHi(i)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${i === hi ? "bg-indigo-50" : ""} ${o.value === value ? "font-semibold text-indigo-700" : "text-gray-700"}`}>
                <span className="flex-1 truncate">{o.label}</span>
                {o.sub && <span className="shrink-0 text-[10px] text-gray-400">{o.sub}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Mağaza İstatistikleri görünümü ----
const SHOP_COLS: { key: keyof ErankShopStat; label: string; fmt: (s: ErankShopStat) => string; hint?: string }[] = [
  { key: "listings", label: "Listeler", fmt: (s) => fmtInt(s.listings) },
  { key: "newCount", label: "Yeni", fmt: (s) => `${fmtInt(s.newCount)} (%${(s.launchShare * 100).toFixed(0)})`, hint: "Pencere içinde açılmış liste (katalog payı)" },
  { key: "newWithSales", label: "Satan yeni", fmt: (s) => fmtInt(s.newWithSales) },
  { key: "newHitRate", label: "İsabet", fmt: (s) => `%${(s.newHitRate * 100).toFixed(0)}`, hint: "Satan yeni / toplam yeni" },
  { key: "newVelocity", label: "Yeni hız", fmt: (s) => s.newVelocity.toFixed(2), hint: "Yeni listelerin ort. satış/gün" },
  { key: "avgDaysToSale", label: "~İlk satış", fmt: (s) => (s.avgDaysToSale != null ? `${s.avgDaysToSale.toFixed(1)}g` : "-"), hint: "≈ yaş/satış — küçük = hızlı" },
  { key: "newRevenue", label: "Yeni ciro", fmt: (s) => fmtMoney(s.newRevenue) },
  { key: "avgPrice", label: "Ort. fiyat", fmt: (s) => fmtMoney(s.avgPrice) },
  { key: "momentum", label: "Momentum", fmt: (s) => s.momentum.toFixed(2), hint: "Yeni liste yapıp satan mağaza gücü" },
];

function InsightText({ text }: { text: string }) {
  return (
    <div className="space-y-1 text-sm leading-relaxed text-gray-700">
      {text.split("\n").map((line, i) => {
        const t = line.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (/^#{1,6}\s/.test(t)) return <div key={i} className="mt-3 text-[15px] font-bold text-gray-900">{t.replace(/^#{1,6}\s/, "")}</div>;
        if (/^[-*]\s/.test(t)) return <div key={i} className="ml-3 flex gap-2"><span className="text-indigo-400">•</span><span>{t.replace(/^[-*]\s/, "")}</span></div>;
        return <div key={i}>{t}</div>;
      })}
    </div>
  );
}

const AI_MODELS: { id: string; label: string }[] = [
  { id: "anthropic/claude-sonnet-4.6", label: "Claude Sonnet 4.6 (hızlı, önerilen)" },
  { id: "anthropic/claude-opus-4.6", label: "Claude Opus 4.6 (en güçlü)" },
  { id: "openai/gpt-5.4", label: "GPT-5.4" },
  { id: "openai/gpt-5.3-chat", label: "GPT-5.3 Chat" },
  { id: "google/gemini-3-flash-preview", label: "Gemini 3 Flash (ucuz/hızlı)" },
];

function ShopsView({ data, loading, statWindow, setStatWindow, aiInsight, aiLoading, aiError, onRunAi, onOpenShop }: {
  data?: ErankShopStatsResponse; loading: boolean; statWindow: number; setStatWindow: (n: number) => void;
  aiInsight: string; aiLoading: boolean; aiError: string; onRunAi: (model?: string) => void; onOpenShop: (s: string) => void;
}) {
  const [sortKey, setSortKey] = useState<keyof ErankShopStat>("momentum");
  const [minNew, setMinNew] = useState<number>(1);
  const [aiModel, setAiModel] = useState<string>(() => localStorage.getItem("erc-erank-ai-model") || AI_MODELS[0].id);
  useEffect(() => { localStorage.setItem("erc-erank-ai-model", aiModel); }, [aiModel]);
  const shops = data?.shops ?? [];
  const sorted = useMemo(() => {
    const f = shops.filter((s) => s.newCount >= minNew);
    return [...f].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (sortKey === "avgDaysToSale") { // küçük daha iyi; null en sona
        const an = av == null ? Infinity : (av as number), bn = bv == null ? Infinity : (bv as number);
        return an - bn;
      }
      return (Number(bv) || 0) - (Number(av) || 0);
    });
  }, [shops, sortKey, minNew]);

  const top = (key: keyof ErankShopStat, min = 0, asc = false) =>
    [...shops.filter((s) => s.newCount >= min)].sort((a, b) => {
      const av = Number(a[key]) || (asc ? Infinity : 0), bv = Number(b[key]) || (asc ? Infinity : 0);
      return asc ? av - bv : bv - av;
    }).slice(0, 3);

  const cards: { title: string; icon: string; list: ErankShopStat[]; val: (s: ErankShopStat) => string }[] = [
    { title: "En yüksek momentum", icon: "🚀", list: top("momentum"), val: (s) => s.momentum.toFixed(2) },
    { title: "En çok yeni liste", icon: "🆕", list: top("newCount"), val: (s) => `${fmtInt(s.newCount)} yeni` },
    { title: "En yüksek isabet", icon: "🎯", list: top("newHitRate", 3), val: (s) => `%${(s.newHitRate * 100).toFixed(0)}` },
    { title: "En hızlı ilk satış", icon: "⚡", list: [...shops.filter((s) => s.newWithSales >= 2 && s.avgDaysToSale != null)].sort((a, b) => (a.avgDaysToSale! - b.avgDaysToSale!)).slice(0, 3), val: (s) => `${s.avgDaysToSale!.toFixed(1)}g` },
  ];

  if (loading) return <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor…</div>;
  if (shops.length === 0) return <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center text-sm text-gray-400">Henüz veri yok. Eklentiyle mağaza çek veya CSV yükle.</div>;

  return (
    <div className="space-y-4">
      {/* Kontrol */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <span className="text-sm font-medium text-gray-700">"Yeni liste" penceresi:</span>
        {[7, 14, 30, 60, 90].map((w) => (
          <button key={w} onClick={() => setStatWindow(w)} className={`rounded-lg px-2.5 py-1 text-xs font-medium ${statWindow === w ? "bg-indigo-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>{w} gün</button>
        ))}
        <span className="ml-auto text-xs text-gray-400">{fmtInt(data?.totalShops ?? 0)} mağaza · {fmtInt(data?.totalListings ?? 0)} listing</span>
      </div>

      {/* Liderlik kartları */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.title} className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="mb-2 text-xs font-semibold text-gray-600">{c.icon} {c.title}</div>
            <div className="space-y-1">
              {c.list.length === 0 ? <div className="text-xs text-gray-300">—</div> : c.list.map((s, i) => (
                <button key={s.shopName} onClick={() => onOpenShop(s.shopName)} className="flex w-full items-center justify-between gap-2 text-left text-xs hover:text-indigo-600">
                  <span className="truncate"><span className="text-gray-400">{i + 1}.</span> {s.shopName}</span>
                  <span className="shrink-0 font-semibold text-gray-700">{c.val(s)}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* AI Analiz */}
      <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-indigo-900">🤖 AI Analiz</div>
          <div className="flex items-center gap-2">
            <select value={aiModel} onChange={(e) => setAiModel(e.target.value)} className="rounded-lg border border-indigo-200 bg-white px-2 py-1.5 text-xs text-gray-700">
              {AI_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <button onClick={() => onRunAi(aiModel)} disabled={aiLoading} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {aiLoading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analiz ediliyor…</> : <>{aiInsight ? "Yeniden analiz et" : "Bu verileri AI ile analiz et"}</>}
            </button>
          </div>
        </div>
        {aiError && <div className="mt-2 text-xs text-red-500">{aiError}</div>}
        {aiInsight && <div className="mt-3 rounded-lg bg-white p-3"><InsightText text={aiInsight} /></div>}
        {!aiInsight && !aiLoading && <div className="mt-2 text-xs text-indigo-700/70">Yükselen mağazaları, hızlı satan yeni listelerdeki ortak kalıpları (fiyat/tag/tema) ve sana özel önerileri çıkarır.</div>}
      </div>

      {/* Tablo */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
          <span>Min. yeni liste:</span>
          {[1, 3, 5, 10].map((m) => (
            <button key={m} onClick={() => setMinNew(m)} className={`rounded px-1.5 py-0.5 ${minNew === m ? "bg-indigo-600 text-white" : "hover:bg-gray-200"}`}>{m}</button>
          ))}
          <span className="ml-auto">{fmtInt(sorted.length)} mağaza</span>
        </div>
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 bg-gray-100 text-left text-[11px] uppercase text-gray-500 shadow-sm">
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Mağaza</th>
                {SHOP_COLS.map((c) => (
                  <th key={c.key} onClick={() => setSortKey(c.key)} title={c.hint}
                    className={`cursor-pointer whitespace-nowrap px-3 py-2 text-right font-medium hover:text-indigo-600 ${sortKey === c.key ? "text-indigo-600" : ""}`}>
                    {c.label}{sortKey === c.key ? " ↓" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map((s, i) => (
                <tr key={s.shopName} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => onOpenShop(s.shopName)} className="font-medium text-gray-800 hover:text-indigo-600">{s.shopName}</button>
                  </td>
                  {SHOP_COLS.map((c) => (
                    <td key={c.key} className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{c.fmt(s)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---- Tag Analizi görünümü ----
const TAG_COLS = [
  { key: "count", label: "Kaç listing" },
  { key: "totalSales", label: "Top. Satış" },
  { key: "totalRevenue", label: "Top. Ciro", money: true },
  { key: "totalViews", label: "Top. Gör." },
  { key: "avgViews", label: "Ort. Gör." },
  { key: "avgSales", label: "Ort. Satış" },
  { key: "avgPrice", label: "Ort. Fiyat", money: true },
  { key: "avgAge", label: "Ort. Yaş" },
] as const;

function TagsView({ data, loading, tagSort, setTagSort, onOpenTag }: {
  data?: ErankTagsResponse; loading: boolean; tagSort: string; setTagSort: (s: string) => void; onOpenTag: (t: string) => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const tags = data?.data ?? [];

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(null), 1200); }).catch(() => {});
  };
  const togglePick = (t: string) => setPicked((p) => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const pickedList = tags.filter((t) => picked.has(t.tag)).map((t) => t.tag);

  if (loading) return <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor…</div>;
  if (tags.length === 0) return <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center text-sm text-gray-400">Tag bulunamadı. Önce CSV yükle veya filtreyi gevşet.</div>;

  return (
    <div className="space-y-3">
      {picked.size > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50/60 px-3 py-2">
          <span className="text-xs font-medium text-indigo-700">{picked.size} tag seçili:</span>
          <span className="flex-1 truncate text-xs text-gray-600">{pickedList.join(", ")}</span>
          <button onClick={() => copy(pickedList.join(", "), "__picked")} className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700">
            {copied === "__picked" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Kopyala
          </button>
          <button onClick={() => setPicked(new Set())} className="text-gray-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
          <span>{data?.totalTags ?? tags.length} benzersiz tag · {data?.listingsScanned ?? 0} listing tarandı</span>
          <span>Etiket kutucuğunu işaretle → toplu kopyala</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 bg-gray-100 text-left text-[11px] uppercase text-gray-500 shadow-sm">
                <th className="px-3 py-2 font-medium">Tag</th>
                {TAG_COLS.map((c) => (
                  <th key={c.key} onClick={() => setTagSort(c.key)}
                    className="cursor-pointer whitespace-nowrap px-3 py-2 text-right font-medium hover:text-indigo-600">
                    {c.label}{tagSort === c.key ? " ↓" : ""}
                  </th>
                ))}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tags.map((t) => (
                <tr key={t.tag} className="hover:bg-gray-50/60">
                  <td className="px-3 py-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={picked.has(t.tag)} onChange={() => togglePick(t.tag)} className="rounded border-gray-300" />
                      <button onClick={() => onOpenTag(t.tag)} className="text-left font-medium text-gray-800 hover:text-indigo-600" title="Bu tag'i içeren listeleri gör">{t.tag}</button>
                      {t.tag.length > 20 && <span className="rounded bg-amber-100 px-1 text-[9px] font-semibold text-amber-700" title="Etsy tag limiti 20 karakter">{t.tag.length}kr</span>}
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(t.count)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{fmtInt(t.totalSales)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtMoney(t.totalRevenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(t.totalViews)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(t.avgViews)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.avgSales.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(t.avgPrice)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.avgAge}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => copy(t.tag, t.tag)} title="Tag'i kopyala" className="text-gray-400 hover:text-indigo-600">
                      {copied === t.tag ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const CP_BADGE: Record<string, { label: string; cls: string }> = {
  low: { label: "Telif: Düşük", cls: "bg-green-100 text-green-700" },
  medium: { label: "Telif: Orta", cls: "bg-amber-100 text-amber-700" },
  high: { label: "Telif: Yüksek", cls: "bg-red-100 text-red-700" },
};

function Row({ it, analyzed, selected, onToggleSel, viewed, onView, onToggleFav, showPreview, hidePreview }: {
  it: ErankListing;
  selected: boolean;
  onToggleSel: () => void;
  analyzed?: { analysisId: string; copyright: string | null };
  viewed: boolean;
  onView: () => void;
  onToggleFav: () => void;
  showPreview: (url: string, x: number, y: number) => void;
  hidePreview: () => void;
}) {
  const analyzedId = analyzed?.analysisId;
  const cp = analyzed?.copyright ? CP_BADGE[analyzed.copyright] : null;
  const etsyUrl = `https://www.etsy.com/listing/${it.listingId}`;
  return (
    <tr className={`hover:bg-gray-50/60 ${selected ? "bg-indigo-50/60" : viewed ? "bg-gray-50/40" : ""}`}>
      <td className="px-2 py-2">
        <input type="checkbox" checked={selected} onChange={onToggleSel} className="h-4 w-4 cursor-pointer accent-indigo-600" />
      </td>
      {/* Durum: favori + görüldü + analiz durumu ayni sutunda */}
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <button onClick={onToggleFav} title={it.favorite ? "Favoriden çıkar" : "Favoriye ekle"}
            className="rounded p-0.5 hover:bg-amber-50">
            <Star className={`h-4 w-4 ${it.favorite ? "fill-amber-400 text-amber-500" : "text-gray-300 hover:text-amber-400"}`} />
          </button>
          {viewed && <Eye className="h-3.5 w-3.5 text-emerald-500" aria-label="Görüldü" />}
          {analyzedId ? (
            <>
              <Link to={`/analyses/${analyzedId}`} title="Analizi aç (senkron)"
                className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-200">
                <FlaskConical className="h-3 w-3" /> Analiz edildi
              </Link>
              {cp && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cp.cls}`}>{cp.label}</span>}
            </>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-400" title="Bu ürün henüz analiz edilmedi">
              Analiz yok
            </span>
          )}
        </div>
      </td>
      <td className="max-w-[360px] px-3 py-2">
        <div className="flex items-start gap-2">
          <a href={etsyUrl} target="_blank" rel="noopener noreferrer" onClick={onView} className="shrink-0">
            {it.imageUrl ? (
              <img
                src={it.imageUrl}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                onMouseEnter={(e) => showPreview(it.imageUrl!, e.clientX, e.clientY)}
                onMouseMove={(e) => showPreview(it.imageUrl!, e.clientX, e.clientY)}
                onMouseLeave={hidePreview}
                className="h-11 w-11 rounded-md border border-gray-200 object-cover"
              />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-md border border-gray-100 bg-gray-50 text-gray-300">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
            )}
          </a>
          <div className="min-w-0">
            <a href={etsyUrl} target="_blank" rel="noopener noreferrer" onClick={onView}
              className={`line-clamp-2 font-medium hover:text-indigo-600 ${viewed ? "text-gray-400" : "text-gray-800"}`}>{it.title || `Listing ${it.listingId}`}</a>
            {it.tags.length > 0 && <div className="mt-0.5 line-clamp-1 text-[10px] text-gray-400">{it.tags.slice(0, 6).join(", ")}</div>}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">{it.shopName}</td>
      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(it.totalViews)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{it.dailyViews.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}</td>
      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(it.favorites)}</td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{fmtInt(it.estSales)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtMoney(it.estRevenue)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(it.price)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{it.listingAge}</td>
      <td className="px-3 py-2 text-right tabular-nums">{(it.salesPerDay ?? 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}</td>
      <td className="px-3 py-2 text-right tabular-nums">%{(it.conversion ?? 0).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}</td>
      <td className="whitespace-nowrap px-3 py-2 text-[11px]"><EtsyCell it={it} /></td>
      <td className="whitespace-nowrap px-3 py-2 text-[11px] text-gray-400">{fmtErankDate(it.lastUpdated)}</td>
      <td className={`whitespace-nowrap px-3 py-2 text-[11px] ${freshClass(it.updatedAt)}`} title={it.updatedAt ? new Date(it.updatedAt).toLocaleString("tr-TR") : ""}>{fmtDateTime(it.updatedAt)}</td>
      <td className="px-3 py-2">
        <a href={etsyUrl} target="_blank" rel="noopener noreferrer" title="Etsy'de gör" onClick={onView}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50">
          Etsy <ExternalLink className="h-3 w-3" />
        </a>
      </td>
    </tr>
  );
}
